import React, { forwardRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const SearchBar = forwardRef(
  (
    {
      searchType,
      searchTerm,
      onSearchChange,
      onSearchRequest,
      suggestions,
      onSuggestionClick,
      onViewMore,
      lastKey,
      remainingCompanies,
      isDropdownOpen,
      onDropdownToggle,
      onDropdownChange,
      dropdownVisible,
      className = "",
      style = {},
      showDropdown = true,
      isSmallScreen = false,
      dropdownRef,
      placeholder,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const suggestionsContainerRef = React.useRef(null);
    const [viewMoreClicked, setViewMoreClicked] = useState(false);
    const [previousSuggestionsCount, setPreviousSuggestionsCount] = useState(0);

    // Track suggestions count changes to detect when new records are loaded
    useEffect(() => {
      if (suggestions.length > previousSuggestionsCount && viewMoreClicked) {
        // New records were loaded, scroll to show them
        setTimeout(() => {
          if (suggestionsContainerRef.current) {
            const container = suggestionsContainerRef.current;
            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;
            const maxScrollTop = scrollHeight - clientHeight;

            // Smooth scroll to show the new records
            container.scrollTo({
              top: maxScrollTop,
              behavior: "smooth",
            });

            // Add a subtle highlight effect to the new records
            const newItems = container.querySelectorAll(".suggestion-item");
            newItems.forEach((item, index) => {
              if (index >= previousSuggestionsCount) {
                item.style.backgroundColor = "#f0f8ff";
                // item.style.transform = "translateX(5px)";

                // Remove the highlight after animation
                setTimeout(() => {
                  item.style.backgroundColor = "transparent";
                  // item.style.transform = "translateX(0)";
                }, 2000);
              }
            });
          }
        }, 150);

        // Reset the clicked state after a delay
        setTimeout(() => {
          setViewMoreClicked(false);
        }, 100);
      }
      setPreviousSuggestionsCount(suggestions.length);
    }, [suggestions.length, previousSuggestionsCount, viewMoreClicked]);

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        onSearchRequest();
      }
    };

    const handleDropdownItemClick = (value) => {
      onDropdownChange(value);
      setTimeout(() => {
        onDropdownToggle();
      }, 0);
    };

    const handleViewMoreClick = async () => {
      setViewMoreClicked(true);
      await onViewMore();
    };

    return (
      <div
        className={`d-flex align-items-center ${className}`}
        style={{ gap: "8px" }}
      >
        <style>
          {`
          @keyframes fadeInOut {
            0% { opacity: 0; transform: translateY(-10px); }
            20% { opacity: 1; transform: translateY(0); }
            80% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-10px); }
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
        </style>
        {showDropdown && (
          <div className="dropdown" ref={dropdownRef}>
            <button
              className={`btn ${
                isSmallScreen
                  ? "btn-small-custom_toggle"
                  : "btn-custom_toggle gap-2"
              } dropdown-toggle d-flex align-items-center ${
                isSmallScreen ? "me-2" : ""
              } justify-content-center`}
              type="button"
              onClick={onDropdownToggle}
              aria-expanded={isDropdownOpen}
            >
              {searchType.charAt(0).toUpperCase() + searchType.slice(1)}
              {/* <span className="ms-auto"> */}
              <span>
                {isDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
              </span>
            </button>
            <ul className={`dropdown-menu ${isDropdownOpen ? "show" : ""}`}>
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => handleDropdownItemClick("managers")}
                >
                  Managers
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => handleDropdownItemClick("company")}
                >
                  Companies
                </button>
              </li>
            </ul>
          </div>
        )}

        <div
          className="searchbar searchbar_sm_screen flex-grow-1"
          style={{
            position: "relative",
            ...style,
          }}
          ref={ref}
        >
          <input
            type="text"
            className="form-control input_search_style"
            placeholder={placeholder || `Search ${searchType}`}
            value={searchTerm}
            onChange={onSearchChange}
            onKeyDown={handleKeyDown}
            style={{
              backgroundColor: "#00685C",
              color: "white",
              border: "none",
              fontSize: isSmallScreen ? "16px" : "1em",
              padding: isSmallScreen ? "6px 0px" : undefined,
              height: isSmallScreen ? "28.5px" : undefined,
              lineHeight: isSmallScreen ? "1.2" : undefined,
              transform: isSmallScreen ? "scale(0.80)" : undefined,
            }}
          />
          <i
            className="bi bi-search fs-6"
            style={{ color: "white", cursor: "pointer" }}
            onClick={onSearchRequest}
          />

          {(dropdownVisible || isSmallScreen) &&
            searchTerm.trim().length > 0 && (
              <ul
                className="suggestion-box"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: "white",
                  listStyle: "none",
                  padding: "10px",
                  margin: 0,
                  borderRadius: "5px",
                  zIndex: 1000,
                  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                  cursor: "pointer",
                }}
              >
                <div
                  ref={suggestionsContainerRef}
                  style={{
                    maxHeight: "292px",
                    overflowY: "auto",
                    position: "relative",
                    border:
                      viewMoreClicked &&
                      suggestions.length > previousSuggestionsCount
                        ? "2px solid #4caf50"
                        : "none",
                    borderRadius: "5px",
                    transition: "border 0.3s ease",
                  }}
                >
                  {viewMoreClicked &&
                    suggestions.length > previousSuggestionsCount && (
                      <div
                        style={{
                          position: "absolute",
                          top: "0",
                          right: "10px",
                          background: "#4caf50",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "0.75em",
                          fontWeight: "bold",
                          zIndex: 1,
                          animation: "fadeInOut 2s ease-in-out",
                        }}
                      >
                        New!
                      </div>
                    )}
                  {suggestions.length > 0 ? (
                    suggestions.map((item, index) => (
                      <li
                        key={item?.id}
                        onClick={() => onSuggestionClick(item)}
                        style={{
                          padding: "7px 0px",
                          cursor: "pointer",
                          transition: "all 0.3s ease-in-out",
                          borderBottom: "1px solid #ddd",
                          display: "flex",
                          alignItems: "center",
                          gap: "0px",
                          backgroundColor:
                            viewMoreClicked && index >= previousSuggestionsCount
                              ? "#f0f8ff"
                              : "transparent",
                          transform:
                            viewMoreClicked && index >= previousSuggestionsCount
                              ? "translateX(5px)"
                              : "translateX(0)",
                        }}
                        className="suggestion-item"
                      >
                        {item?.company_logo_url && (
                          <div
                            style={{
                              flexShrink: 0,
                              marginRight: "-5px",
                              marginLeft:
                                window.innerWidth < 993 ? "-8px" : "0px",
                            }}
                          >
                            <img
                              src={item.company_logo_url}
                              alt="Company Logo"
                              style={{
                                width: "24px",
                                height: "24px",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                          </div>
                        )}
                        <div style={{ flex: 1, overflow: "hidden" }}>
                          <p
                            className="mb-0 search_suggestions_name_bold text-truncate"
                            style={{
                              fontWeight: "bold",
                              color: "#333",
                              fontSize:
                                window.innerWidth < 993 ? "0.90em" : "1em",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                            }}
                          >
                            {item?.name}
                          </p>
                          {searchType === "managers" ? (
                            <div className="dis-flex justify-content-between display-block-mt-4">
                              <div className="company-position-container">
                                <span
                                  className="mb-0 search_suggestions_name text-truncate company-name-line"
                                  style={{
                                    fontSize: "0.9em",
                                    color: "#555",
                                    maxWidth: "100%",
                                  }}
                                >
                                  {item?.company_name}
                                </span>
                                <span className="company-position-separator">
                                  {" "}
                                  -{" "}
                                </span>
                                <span
                                  className="mb-0 search_suggestions_name text-truncate position-line"
                                  style={{
                                    fontSize: "0.9em",
                                    color: "#555",
                                    maxWidth: "100%",
                                  }}
                                >
                                  {item?.position}
                                </span>
                              </div>
                              <p
                                className="location-text mb-0 search_suggestions_name text-truncate"
                                style={{
                                  fontSize: "0.9em",
                                  color: "#777",
                                  maxWidth: "30%",
                                  textAlign: "right",
                                }}
                              >
                                {item?.location}
                              </p>
                            </div>
                          ) : (
                            <div className="dis-flex justify-content-between display-block-mt-4">
                              <p
                                className="mb-0 search_suggestions_name text-truncate"
                                style={{
                                  fontSize: "0.9em",
                                  color: "#555",
                                  maxWidth: "70%",
                                }}
                              >
                                {item?.location}
                              </p>
                              <p
                                className="mb-0 search_suggestions_name text-truncate location-text"
                                style={{
                                  fontSize: "0.9em",
                                  color: "#777",
                                  maxWidth: "30%",
                                  textAlign: "right",
                                }}
                              >
                                {item?.industry}
                              </p>
                            </div>
                          )}
                        </div>
                      </li>
                    ))
                  ) : (
                    <li
                      style={{
                        padding: "7px",
                        color: "#888",
                        textAlign: "center",
                        marginBottom: 0,
                      }}
                    >
                      No results found
                    </li>
                  )}
                </div>

                {/* View More button */}
                {(lastKey ||
                  (searchType === "company" &&
                    remainingCompanies.length > 0)) && (
                  <li
                    style={{
                      padding: "10px",
                      textAlign: "center",
                      background: viewMoreClicked ? "#e3f2fd" : "#f8f9fa",
                      cursor: "pointer",
                      borderTop: "1px solid #ddd",
                      fontWeight: "bold",
                      color: viewMoreClicked ? "#1976d2" : "#007bff",
                      transition: "all 0.3s ease",
                      transform: viewMoreClicked ? "scale(1.02)" : "scale(1)",
                      boxShadow: viewMoreClicked
                        ? "0 2px 8px rgba(25, 118, 210, 0.3)"
                        : "none",
                    }}
                    onClick={handleViewMoreClick}
                  >
                    {viewMoreClicked ? (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            border: "2px solid #1976d2",
                            borderTop: "2px solid transparent",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        />
                        Loading...
                      </span>
                    ) : (
                      "View More"
                    )}
                  </li>
                )}

                {/* Add manager profile link */}
                {searchType === "managers" && (
                  <li style={{ padding: "5px 0", textAlign: "center" }}>
                    <p
                      className="fw-bold text-primary"
                      style={{
                        textDecoration: "none",
                        cursor: "pointer",
                        margin: 0,
                        padding: 0,
                      }}
                      onClick={() => {
                        navigate("/add-manager-profile");
                        onSuggestionClick({ id: "clear" });
                      }}
                    >
                      Can't find your manager? Create new manager profile
                    </p>
                  </li>
                )}
              </ul>
            )}
        </div>
      </div>
    );
  }
);

SearchBar.displayName = "SearchBar";

export default SearchBar;
